import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchBySlug } from '../lib/cms';
import { Phone, Mail, MapPin, Clock, ShieldCheck } from 'lucide-react';
import ContactForm from '../components/ContactForm';

export default function Contact() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchBySlug('kontakt');
      if (data) {
        setPageData(data);
      }
      setLoading(false);
    }
    loadPage();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
      </div>
    );
  }

  const title = pageData?.title?.da || 'Kontakt os';
  const metaTitle = pageData?.meta_title?.da || 'Kontakt os - KBS Tømrer – Montage ApS';
  const metaDesc = pageData?.meta_description?.da || 'Har du lyst til at høre mere om, hvad vi kan tilbyde dig? Vi tager gerne en uforpligtende snak med dig – ring eller skriv til os i dag!';
  const heroImage = pageData?.hero_image?.[0] || 'https://assets.auxodev.dk/assets/10033775-ad6c-49dd-a719-01817fe062a0/kontakt.jpg';

  // Optimer hero-billede via Cloudflare resizing (~1600px bredde)
  const optimizedHero = heroImage.startsWith('https://assets.auxodev.dk')
    ? heroImage.replace('https://assets.auxodev.dk/assets/', 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/')
    : heroImage;

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <html lang="da" />
      </Helmet>

      {/* Hero Header */}
      <section className="relative bg-slate-950 text-white py-20 lg:py-28">
        <div className="absolute inset-0 z-0">
          <img
            src={optimizedHero}
            alt={title}
            className="h-full w-full object-cover object-center opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-slate-950 to-slate-950" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            {title}
          </h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Vi tager gerne en uforpligtende snak om dit projekt. Ring eller skriv til os i dag, så vender vi hurtigt tilbage.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-white flex-1">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Contact Info & Details */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <h2 className="font-display text-2xl font-bold text-brand-charcoal mb-4">
                  Lad os få en snak om dit byggeprojekt
                </h2>
                <div
                  className="prose prose-slate text-slate-600 leading-relaxed text-sm sm:text-base mb-6"
                  dangerouslySetInnerHTML={{ __html: pageData?.content?.da || '' }}
                />
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                {/* Phone */}
                <a
                  href="tel:51219030"
                  className="flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50/50 p-4 hover:border-brand-primary/30 hover:shadow-md transition-all group"
                >
                  <div className="rounded-lg bg-brand-primary/10 p-2.5 text-brand-primary group-hover:bg-brand-primary group-hover:text-white transition-colors">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase">Telefon</p>
                    <p className="text-base font-bold text-brand-charcoal group-hover:text-brand-primary transition-colors">
                      51 21 90 30
                    </p>
                  </div>
                </a>

                {/* Email */}
                <a
                  href="mailto:kbsoerensen@outlook.dk"
                  className="flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50/50 p-4 hover:border-brand-primary/30 hover:shadow-md transition-all group"
                >
                  <div className="rounded-lg bg-brand-primary/10 p-2.5 text-brand-primary group-hover:bg-brand-primary group-hover:text-white transition-colors">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-400 uppercase">E-mail</p>
                    <p className="text-sm font-bold text-brand-charcoal group-hover:text-brand-primary transition-colors truncate">
                      kbsoerensen@outlook.dk
                    </p>
                  </div>
                </a>

                {/* Address */}
                <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                  <div className="rounded-lg bg-brand-primary/10 p-2.5 text-brand-primary">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase">Adresse</p>
                    <p className="text-sm font-bold text-brand-charcoal">
                      Balskærvej 26, 8340 Malling
                    </p>
                  </div>
                </div>

                {/* Opening Hours */}
                <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                  <div className="rounded-lg bg-brand-primary/10 p-2.5 text-brand-primary">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase">Åbningstider</p>
                    <p className="text-sm font-bold text-brand-charcoal">
                      Mandag - Fredag: 07:00 - 17:00
                    </p>
                  </div>
                </div>
              </div>

              {/* Trust Badge */}
              <div className="rounded-2xl bg-brand-primary/5 border border-brand-primary/10 p-6 flex items-start gap-4">
                <ShieldCheck className="h-8 w-8 text-brand-primary shrink-0" />
                <div>
                  <h4 className="font-bold text-brand-charcoal text-sm">Uforpligtende tilbud</h4>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    Det koster ingenting at spørge. Vi kommer gerne ud og besigtiger din opgave i Malling, Odder, Skanderborg, Ry eller Århus og giver et skriftligt, uforpligtende tilbud.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
